import RuntimeException from '@/exceptions/RuntimeException'
import resetStore from '@/components/use/resetStore'
import Log from '@/utilities/Log'
import config from '@/config'
import SupportableException from '@/exceptions/SupportableException'
import RecoverableException from '@/exceptions/RecoverableException'
import Honeybadger from '@honeybadger-io/js'
import { useErrorStore } from '@/storage/error'
import { AxiosError } from 'axios'
import InvalidStoreException from '@/exceptions/InvalidStoreException'

export default async function handleError(err: Error) {
  const honeybadger = Honeybadger.configure(config.honeyBadger)
  let handled = true
  const errorMessage = resolveErrorMessage(err)
  switch (err.constructor.name) {
    case 'MaintenanceModeException':
      useErrorStore().setType('information')
      break
    case 'NewMajorVersionException':
      useErrorStore().setRequiresReload(true)
      useErrorStore().setType('warning')
      break
    default:
      useErrorStore().setType('danger')
      break
  }
  useErrorStore().setMessage(errorMessage)
  useErrorStore().setIsSupportable(err instanceof SupportableException)

  if (!(err instanceof RecoverableException)) {
    honeybadger?.notify(err)
  }

  if (err instanceof RuntimeException || err instanceof InvalidStoreException) {
    resetStore()
    Log.storeReset('handleError.ts', 'handleError')
    handled = false
  }

  return handled
}

function resolveErrorMessage(error: unknown): string {
  const defaultMessage = 'An unknown error occurred.'
  if (error instanceof AxiosError) {
    if (isApiError(error)) {
      return error.response.data.errors[0].message ?? defaultMessage
    }
    return error.response?.data?.message ?? defaultMessage
  }

  if (typeof error === 'string') {
    return error
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return (error as { message: string }).message
  }

  return defaultMessage
}

function isApiError(
  error: AxiosError<{
    errors:
      | { code: number; message: string }[]
      | {
          [key: string]: string[]
        }
    source?: 'capture' | 'data'
  }>,
): error is AxiosError & {
  response: { data: { errors: Array<{ code: number; message: string }> } }
} {
  return (
    !!error.response?.data &&
    typeof error.response.data === 'object' &&
    'source' in error.response.data &&
    'errors' in error.response.data &&
    Array.isArray(error.response.data.errors) &&
    error.response.data.errors.length > 0
  )
}
